import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import {
  Search, Eye, CheckCircle2, XCircle, Sparkles, Trash2,
  RefreshCw, ChevronDown, X, Download, Pencil,
} from "lucide-react";
import SubmissionDetailDrawer from "./SubmissionDetailDrawer";
import CustomStatusModal from "./CustomStatusModal";

const STATUS_PILL = {
  submitted:    { bg: "bg-sky-100",     fg: "text-sky-700",     label: "RECEIVED" },
  under_review: { bg: "bg-amber-100",   fg: "text-amber-700",   label: "REVIEW" },
  approved:     { bg: "bg-emerald-100", fg: "text-emerald-700", label: "APPROVED" },
  rejected:     { bg: "bg-rose-100",    fg: "text-rose-700",    label: "REJECTED" },
  paid:         { bg: "bg-violet-100",  fg: "text-violet-700",  label: "PAID" },
  duplicate:    { bg: "bg-purple-100",  fg: "text-purple-700",  label: "DUPLICATE" },
};

const STATUS_OPTIONS = [
  { v: "all", label: "All Statuses" },
  { v: "submitted", label: "Received" },
  { v: "under_review", label: "Under review" },
  { v: "approved", label: "Approved" },
  { v: "rejected", label: "Rejected" },
  { v: "paid", label: "Paid" },
  { v: "duplicate", label: "Duplicate" },
];

const AUTO_REFRESH_MS = 3 * 60 * 1000;
const PAGE_SIZE = 50;

function fmtCompactDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminSubmissions() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [qCommitted, setQCommitted] = useState("");
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [customStatusFor, setCustomStatusFor] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (status !== "all") params.status = status;
      if (qCommitted.trim()) params.q = qCommitted.trim();
      const r = await adminApi.get("/admin/submissions", { params });
      setItems(r.data);
      const t = parseInt(r.headers?.["x-total-count"], 10);
      if (!Number.isNaN(t)) setTotal(t);
    } catch {
      toast.error("Could not load submissions");
    } finally {
      setLoading(false);
    }
  }, [status, qCommitted, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [status, qCommitted]);
  useEffect(() => {
    const id = setInterval(load, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const onSearch = (e) => { e.preventDefault(); setQCommitted(q); };

  const quickStatus = async (id, newStatus) => {
    try {
      await adminApi.post(`/admin/submissions/${id}/status`, { status: newStatus });
      toast.success(`Marked as ${newStatus.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteOne = async (id, ref) => {
    if (!window.confirm(`Delete submission ${ref}? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/submissions/${id}`);
      toast.success(`${ref} deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bulkAct = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const verb = action === "delete" ? "delete" : action.replace("_", " ");
    if (!window.confirm(`${verb.toUpperCase()} ${ids.length} submission${ids.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
    try {
      const r = await adminApi.post("/admin/submissions/bulk-action", { ids, action });
      toast.success(`${r.data.action} → ${r.data.affected} of ${r.data.requested}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (status && status !== "all") params.set("status", status);
      if (qCommitted) params.set("q", qCommitted);
      const r = await adminApi.get(`/admin/submissions/export.csv?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `gcnexon-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const firstCard = (s) => {
    if (s.cards && s.cards[0]) return s.cards[0];
    return { brand_name: s.brand_name, balance: s.card_amount };
  };

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 md:p-8" data-testid="admin-submissions">
        {/* Page title */}
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900" data-testid="admin-submissions-title">Submissions</h1>
            <p className="text-slate-500 mt-1 text-sm">Manage every card handed in. {total.toLocaleString()} total · showing {items.length}.</p>
            <div className="mt-3 h-[3px] w-24 rounded-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-emerald-400" />
          </div>
        </div>

        {/* Filter bar */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-3 mb-6 flex flex-col md:flex-row md:items-center gap-3">
          <form onSubmit={onSearch} className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onBlur={() => setQCommitted(q)}
              placeholder="Search by ref#, name, email, or brand…"
              data-testid="admin-search"
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:bg-white"
            />
          </form>
          <div className="relative">
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-status-filter"
              className="appearance-none h-11 px-4 pr-10 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500">
              {STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          <button onClick={load} disabled={loading} data-testid="admin-refresh"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={exportCsv} data-testid="admin-export-csv"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <div className="hidden md:flex items-center gap-2 text-xs text-slate-500">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Auto-refreshes every 3 min
          </div>
        </div>

        {/* Bulk toolbar */}
        {selected.size > 0 && (
          <div className="sticky top-[72px] z-20 mb-4 flex flex-wrap items-center gap-2 bg-slate-900 text-white rounded-2xl px-5 py-3 shadow-lg" data-testid="bulk-actions-bar">
            <span className="text-sm font-semibold">{selected.size} selected</span>
            <div className="h-5 w-px bg-white/20 mx-2" />
            <button onClick={() => bulkAct("approve")} data-testid="bulk-approve" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-xs font-semibold">
              <CheckCircle2 className="w-3.5 h-3.5" /> Approve all
            </button>
            <button onClick={() => bulkAct("reject")} data-testid="bulk-reject" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-rose-500 hover:bg-rose-600 text-xs font-semibold">
              <XCircle className="w-3.5 h-3.5" /> Reject all
            </button>
            <button onClick={() => bulkAct("mark_paid")} data-testid="bulk-paid" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-sky-500 hover:bg-sky-600 text-xs font-semibold">
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark paid
            </button>
            <button onClick={() => bulkAct("under_review")} data-testid="bulk-review" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-amber-500 hover:bg-amber-600 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> Under review
            </button>
            <button onClick={() => bulkAct("delete")} data-testid="bulk-delete" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/10 hover:bg-rose-600 text-xs font-semibold">
              <Trash2 className="w-3.5 h-3.5" /> Delete all
            </button>
            <button onClick={() => setSelected(new Set())} data-testid="bulk-clear" className="ml-auto inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold text-white/70 hover:text-white hover:bg-white/10">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white border-b border-slate-100">
                <tr className="text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-4 w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="admin-select-all" className="rounded border-slate-300" />
                  </th>
                  <th className="text-left px-2 py-4 font-semibold">Ref #</th>
                  <th className="text-left px-5 py-4 font-semibold">Customer</th>
                  <th className="text-left px-5 py-4 font-semibold">Card</th>
                  <th className="text-left px-5 py-4 font-semibold">Date</th>
                  <th className="text-left px-5 py-4 font-semibold">Status</th>
                  <th className="text-right px-5 py-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => {
                  const pill = STATUS_PILL[s.status] || STATUS_PILL.submitted;
                  const card = firstCard(s);
                  const canApproveReject = ["submitted", "under_review"].includes(s.status);
                  return (
                    <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors" data-testid={`admin-row-${s.reference}`}>
                      <td className="px-5 py-4">
                        <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)} className="rounded border-slate-300" />
                      </td>
                      <td className="px-2 py-4">
                        <button onClick={() => setOpenId(s.id)} data-testid={`admin-open-${s.reference}`}
                          className="font-mono font-bold text-sky-600 hover:text-sky-800 hover:underline">{s.reference}</button>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-semibold text-slate-900">{s.customer_name || s.full_name || "—"}</div>
                        <div className="text-xs text-slate-500">{s.customer_email || s.email}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-slate-900">
                          <span className="capitalize">{card.brand_name || "—"}</span>{" "}
                          <span className="font-mono text-slate-600">${Number(card.balance || 0).toFixed(2)}</span>
                        </div>
                        {s.cards?.length > 1 && <div className="text-[11px] text-slate-400">+{s.cards.length - 1} more</div>}
                      </td>
                      <td className="px-5 py-4 text-slate-500 text-xs whitespace-nowrap">{fmtCompactDate(s.created_at)}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wider ${pill.bg} ${pill.fg}`}>{pill.label}</span>
                        {s.edited_of_ref && s.status !== "duplicate" && (
                          <span title={`Customer re-submitted with corrections to ${s.edited_of_ref}`} data-testid={`admin-edited-${s.reference}`}
                            className="ml-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold tracking-wider bg-blue-50 text-blue-700 border border-blue-200">
                            <Pencil className="w-2.5 h-2.5" /> EDITED
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => setOpenId(s.id)} title="View details" data-testid={`admin-view-${s.reference}`}
                            className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Eye className="w-4 h-4" /></button>
                          {canApproveReject && (
                            <>
                              <button onClick={() => quickStatus(s.id, "approved")} data-testid={`admin-approve-${s.reference}`}
                                className="inline-flex items-center gap-1 px-3 h-8 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                              </button>
                              <button onClick={() => quickStatus(s.id, "rejected")} data-testid={`admin-reject-${s.reference}`}
                                className="inline-flex items-center gap-1 px-3 h-8 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold">
                                <XCircle className="w-3.5 h-3.5" /> Reject
                              </button>
                            </>
                          )}
                          <button onClick={() => setCustomStatusFor(s)} title="Custom status update" data-testid={`admin-custom-${s.reference}`}
                            className="inline-flex items-center gap-1 px-3 h-8 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold">
                            <Sparkles className="w-3.5 h-3.5" /> Custom
                          </button>
                          <button onClick={() => deleteOne(s.id, s.reference)} title="Delete" data-testid={`admin-delete-${s.reference}`}
                            className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && !loading && <tr><td colSpan={7} className="py-16 text-center text-slate-400 text-sm">No submissions match your filters.</td></tr>}
                {loading && items.length === 0 && <tr><td colSpan={7} className="py-16 text-center text-slate-400 text-sm">Loading…</td></tr>}
              </tbody>
            </table>
          </div>
          {total > 0 && <Pagination page={page} pageSize={PAGE_SIZE} total={total} pageItemsCount={items.length} onChange={setPage} />}
        </div>
      </div>

      {openId && <SubmissionDetailDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
      {customStatusFor && <CustomStatusModal submission={customStatusFor} onClose={() => setCustomStatusFor(null)} onSaved={load} />}
    </AdminLayout>
  );
}

function Pagination({ page, pageSize, total, pageItemsCount, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, totalPages - 1);
  const from = total === 0 ? 0 : current * pageSize + 1;
  const to = current * pageSize + pageItemsCount;

  const pages = [];
  const push = (v) => { if (pages[pages.length - 1] !== v) pages.push(v); };
  push(1);
  for (let p = current - 1; p <= current + 2; p += 1) {
    if (p >= 2 && p <= totalPages - 1) push(p);
  }
  if (totalPages > 1) push(totalPages);
  pages.sort((a, b) => a - b);
  const withEllipses = [];
  for (let i = 0; i < pages.length; i += 1) {
    if (i > 0 && pages[i] - pages[i - 1] > 1) withEllipses.push("…");
    withEllipses.push(pages[i]);
  }
  const go = (p) => onChange(Math.max(0, Math.min(totalPages - 1, p)));

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-t border-slate-100" data-testid="admin-pagination">
      <div className="text-sm text-slate-500" data-testid="admin-pagination-summary">
        Showing <strong className="text-slate-900">{from}</strong> to <strong className="text-slate-900">{to}</strong> of <strong className="text-slate-900">{total.toLocaleString()}</strong> submissions
      </div>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => go(current - 1)} disabled={current === 0} data-testid="admin-pagination-prev"
          className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Previous</button>
        {withEllipses.map((entry, i) =>
          entry === "…" ? (
            <span key={`gap-${i}`} className="px-2 text-slate-400 text-sm">…</span>
          ) : (
            <button key={entry} type="button" onClick={() => go(entry - 1)} data-testid={`admin-pagination-page-${entry}`}
              className={`min-w-[36px] h-9 px-2.5 rounded-lg text-xs font-semibold transition-all ${
                entry - 1 === current
                  ? "bg-gradient-to-br from-indigo-500 to-indigo-700 text-white shadow-sm"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}>{entry}</button>
          ),
        )}
        <button type="button" onClick={() => go(current + 1)} disabled={current >= totalPages - 1} data-testid="admin-pagination-next"
          className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Next</button>
      </div>
    </div>
  );
}
